import React, { useState, useEffect } from 'react';
import './OverallStats.scss';
import { getAnalyzeActAPIUrl, getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";

const API_BASE_URL = getAPIUrl();

const OverallStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiFetch(`${API_BASE_URL}/monitoring/stats`);
            if (!response.ok) throw new Error('Ошибка загрузки статистики');

            const data = await response.json();
            setStats(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="overall-stats stats-loading">
                <div className="flex items-center">
                    <i className="fas fa-circle-notch fa-2x animate-spin text-blue-500"></i>
                    <span style={{ marginLeft: '0.75rem', color: '#64748b' }}>Загрузка статистики...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="overall-stats stats-error">
                <div>
                    <i className="fas fa-exclamation-triangle"></i> {error}
                    <button onClick={fetchStats}>
                        Повторить
                    </button>
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="overall-stats stats-empty">
                <div className="text-center">
                    <i className="fas fa-chart-bar fa-2x" style={{ marginBottom: '0.5rem' }}></i>
                    <p>Нет данных</p>
                </div>
            </div>
        );
    }

    const { overall, by_job_type } = stats;

    return (
        <div className="overall-stats">
            {/* Header */}
            <div className="stats-header">
                <h2><i className="fas fa-chart-bar"></i> Общая статистика</h2>
                <p>Статистика по всем сессиям в системе</p>
            </div>

            {/* Metric Cards */}
            <div className="p-6">
                <div className="metric-grid">
                    <div className="metric-card blue">
                        <div className="metric-icon"><i className="fas fa-database"></i></div>
                        <div className="metric-info">
                            <h4>Всего сессий</h4>
                            <p>{overall.total_sessions || 0}</p>
                        </div>
                    </div>

                    <div className="metric-card green">
                        <div className="metric-icon"><i className="fas fa-check-circle"></i></div>
                        <div className="metric-info">
                            <h4>Завершено</h4>
                            <p>{overall.completed || 0}</p>
                        </div>
                    </div>

                    <div className="metric-card red">
                        <div className="metric-icon"><i className="fas fa-times-circle"></i></div>
                        <div className="metric-info">
                            <h4>Неудачно</h4>
                            <p>{overall.failed || 0}</p>
                        </div>
                    </div>

                    <div className="metric-card blue">
                        <div className="metric-icon"><i className="fas fa-sync-alt"></i></div>
                        <div className="metric-info">
                            <h4>Выполняется</h4>
                            <p>{overall.running || 0}</p>
                        </div>
                    </div>

                    <div className="metric-card orange">
                        <div className="metric-icon"><i className="fas fa-clock"></i></div>
                        <div className="metric-info">
                            <h4>Ожидает</h4>
                            <p>{overall.pending || 0}</p>
                        </div>
                    </div>
                </div>

                {/* Progress Bars */}
                <div className="progress-group">
                    <div className="progress-item">
                        <div className="progress-label">
                            <span>Процент завершенных</span>
                            <span>{overall.total_sessions ? Math.round((overall.completed / overall.total_sessions) * 100) : 0}%</span>
                        </div>
                        <div className="progress-bar">
                            <div 
                                className="fill green" 
                                style={{ width: `${overall.total_sessions ? (overall.completed / overall.total_sessions) * 100 : 0}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="progress-item">
                        <div className="progress-label">
                            <span>Процент неудачных</span>
                            <span>{overall.total_sessions ? Math.round((overall.failed / overall.total_sessions) * 100) : 0}%</span>
                        </div>
                        <div className="progress-bar">
                            <div 
                                className="fill red" 
                                style={{ width: `${overall.total_sessions ? (overall.failed / overall.total_sessions) * 100 : 0}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                {/* Job Type Table */}
                {by_job_type && by_job_type.length > 0 && (
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">По типам задач</h3>
                        <div className="overflow-x-auto">
                            <table className="stats-table">
                                <thead>
                                    <tr>
                                        <th>Тип задачи</th>
                                        <th>Всего</th>
                                        <th>Завершено</th>
                                        <th>Неудачно</th>
                                        <th>Успешность</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {by_job_type.map((jobType, index) => (
                                        <tr key={index}>
                                            <td>{jobType.job_type || 'Unknown'}</td>
                                            <td>{jobType.count}</td>
                                            <td className="text-green">{jobType.completed}</td>
                                            <td className="text-red">{jobType.failed}</td>
                                            <td>{jobType.count > 0 ? Math.round((jobType.completed / jobType.count) * 100) : 0}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="stats-footer">
                <span>
                    <i className="fas fa-info-circle"></i>
                    Последнее обновление: {new Date().toLocaleTimeString()}
                </span>
                <button onClick={fetchStats}>
                    <i className="fas fa-refresh"></i> Обновить
                </button>
            </div>
        </div>
    );
};

export default OverallStats;
